import { useEffect, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { BadgeCheck, Building2, Camera, ExternalLink, Loader2, MapPin, Plus, Users, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Select, Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs } from '@/components/ui/tabs'
import { VacancyStatusBadge, arrangementLabel, employmentLabel } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import type { Company } from '@/lib/types'
import {
  useAddCompanyMember,
  useCompanyMembers,
  useCreateCompany,
  useMyCompanies,
  useRemoveCompanyLogo,
  useRemoveCompanyMember,
  useUpdateCompany,
  useUploadCompanyLogo,
} from '@/features/company/api'
import { useCompanyVacancies, useCreateVacancy, type VacancyInput } from '@/features/hiring/api'
import { useMarketplaceCategories } from '@/features/marketplace/api'
import { ApiError } from '@/lib/api'
import { formatNumber, formatRupiahShort } from '@/lib/format'

const sizeOptions = ['1-10', '11-50', '51-200', '201-1000', '1000+']

/**
 * LogoPerusahaan menampilkan logo sekaligus menjadi tombol menggantinya.
 *
 * Menyatukan keduanya menghindarkan satu bagian pengaturan tersendiri untuk
 * satu berkas — dan yang ingin diganti orang memang gambar yang sedang
 * dilihatnya, bukan sebuah kolom bernama "logo_url".
 */
function LogoPerusahaan({ company }: { company: Company }) {
  const berkas = useRef<HTMLInputElement>(null)
  const unggah = useUploadCompanyLogo(company.id)
  const hapus = useRemoveCompanyLogo(company.id)
  const toast = useToast()

  const sibuk = unggah.isPending || hapus.isPending

  const pilih = (file: File | undefined) => {
    if (!file) return
    unggah.mutate(file, {
      onSuccess: () => toast.success('Logo diperbarui'),
      onError: (error) => toast.error('Logo gagal diunggah', pesanGalat(error)),
    })
  }

  return (
    <div className="group relative shrink-0">
      <input
        ref={berkas}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => pilih(e.target.files?.[0])}
      />

      {company.logo_url ? (
        <img
          src={company.logo_url}
          alt={`Logo ${company.name}`}
          className="size-14 rounded-2xl border border-line object-cover"
        />
      ) : (
        <span className="flex size-14 items-center justify-center rounded-2xl bg-brand-soft text-brand">
          <Building2 className="size-7" />
        </span>
      )}

      <button
        type="button"
        onClick={() => berkas.current?.click()}
        disabled={sibuk}
        aria-label={company.logo_url ? 'Ganti logo perusahaan' : 'Unggah logo perusahaan'}
        className="absolute -bottom-1.5 -right-1.5 grid size-7 place-items-center rounded-full border-2 border-surface bg-brand text-white shadow-soft transition hover:bg-brand-600 disabled:opacity-60"
      >
        {sibuk ? <Loader2 className="size-3.5 animate-spin" /> : <Camera className="size-3.5" />}
      </button>

      {company.logo_url ? (
        <button
          type="button"
          onClick={() =>
            hapus.mutate(undefined, {
              onSuccess: () => toast.success('Logo dihapus'),
              onError: (error) => toast.error('Gagal menghapus logo', pesanGalat(error)),
            })
          }
          disabled={sibuk}
          aria-label="Hapus logo perusahaan"
          className="absolute -right-1.5 -top-1.5 grid size-6 place-items-center rounded-full border-2 border-surface bg-surface-sunken text-muted opacity-0 transition hover:text-danger focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-60"
        >
          <X className="size-3" />
        </button>
      ) : null}
    </div>
  )
}

function pesanGalat(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return 'Terjadi kesalahan yang tidak terduga.'
}

export function CompanyPage() {
  const { data: companies, isLoading } = useMyCompanies()
  const [tab, setTab] = useState<'lowongan' | 'profil' | 'tim'>('lowongan')
  const [createOpen, setCreateOpen] = useState(false)
  const [vacancyOpen, setVacancyOpen] = useState(false)

  const company = companies?.[0]
  const { data: vacancyData } = useCompanyVacancies(company?.id)
  const vacancies = vacancyData?.vacancies ?? []

  if (isLoading) return <Skeleton className="h-96 rounded-2xl" />

  if (!company) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Perekrutan</h1>
          <p className="mt-1 text-muted">Pasang lowongan atas nama perusahaan Anda.</p>
        </header>

        <Card>
          <EmptyState
            icon={<Building2 className="size-6" />}
            title="Buat profil perusahaan dulu"
            description="Lowongan dipasang atas nama perusahaan, bukan perorangan, supaya pelamar tahu persis siapa yang merekrut."
            action={<Button onClick={() => setCreateOpen(true)}>Buat profil perusahaan</Button>}
          />
        </Card>

        <CompanyDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <LogoPerusahaan company={company} />

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-extrabold tracking-tight text-ink">{company.name}</h1>
              {company.verified ? (
                <Badge tone="brand" icon={<BadgeCheck className="size-3.5" />}>
                  Terverifikasi
                </Badge>
              ) : (
                <Badge tone="warning">Menunggu verifikasi</Badge>
              )}
            </div>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 text-sm text-muted">
              {company.industry ? <span>{company.industry}</span> : null}
              {company.city ? (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="size-3.5" />
                  {company.city}
                </span>
              ) : null}
              {company.size ? <span>{company.size} karyawan</span> : null}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Link to="/perusahaan/$slug" params={{ slug: company.slug }}>
            <Button variant="outline">
              <ExternalLink className="size-4" />
              Halaman publik
            </Button>
          </Link>
          <Button onClick={() => setVacancyOpen(true)}>
            <Plus className="size-4" />
            Pasang lowongan
          </Button>
        </div>
      </header>

      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'lowongan', label: 'Lowongan', count: vacancies.length },
          { value: 'profil', label: 'Profil perusahaan' },
          { value: 'tim', label: 'Tim perekrut' },
        ]}
      />

      {tab === 'lowongan' ? (
        vacancies.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Building2 className="size-6" />}
              title="Belum ada lowongan"
              description="Pasang lowongan pertama Anda; draf dapat disimpan dulu sebelum ditayangkan."
              action={<Button onClick={() => setVacancyOpen(true)}>Pasang lowongan</Button>}
            />
          </Card>
        ) : (
          <div className="space-y-3">
            {vacancies.map((vacancy) => (
              <Card key={vacancy.id}>
                <CardBody className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <VacancyStatusBadge status={vacancy.status} />
                      <Badge tone="brand">{employmentLabel[vacancy.employment_type]}</Badge>
                      <Badge>{arrangementLabel[vacancy.work_arrangement]}</Badge>
                    </div>
                    <h2 className="mt-2 font-bold text-ink">{vacancy.title}</h2>
                    <p className="mt-0.5 text-sm text-muted">
                      {vacancy.salary_visible && vacancy.salary_max
                        ? `${formatRupiahShort(vacancy.salary_min ?? 0)} – ${formatRupiahShort(vacancy.salary_max)} per bulan`
                        : 'Gaji dirundingkan'}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="inline-flex items-center gap-1 text-sm font-semibold text-ink">
                      <Users className="size-4" />
                      {formatNumber(vacancy.application_count)} pelamar
                    </p>
                    <p className="text-xs text-subtle">{formatNumber(vacancy.view_count)} kali dilihat</p>
                    <Link
                      to="/app/perusahaan/lowongan/$vacancyId"
                      params={{ vacancyId: vacancy.id }}
                      className="mt-3 block"
                    >
                      <Button size="sm">Kelola pelamar</Button>
                    </Link>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        )
      ) : null}

      {tab === 'profil' ? <CompanyProfileForm companyId={company.id} /> : null}
      {tab === 'tim' ? <CompanyTeam companyId={company.id} /> : null}

      <VacancyDialog companyId={company.id} open={vacancyOpen} onClose={() => setVacancyOpen(false)} />
    </div>
  )
}

function CompanyDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createCompany = useCreateCompany()
  const toast = useToast()
  const [values, setValues] = useState({ name: '', industry: '', size: '', city: '', website: '', about: '' })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Buat profil perusahaan"
      description="Profil ini yang dilihat pelamar sebelum memutuskan mengirim lamaran."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            loading={createCompany.isPending}
            disabled={values.name.trim().length < 2}
            onClick={() =>
              createCompany.mutate(
                {
                  ...values,
                  website: values.website || undefined,
                  size: values.size || undefined,
                },
                {
                  onSuccess: () => {
                    toast.success('Perusahaan dibuat', 'Anda kini dapat memasang lowongan.')
                    onClose()
                  },
                  onError: (error) =>
                    toast.error('Gagal membuat perusahaan', error instanceof ApiError ? error.message : undefined),
                },
              )
            }
          >
            Simpan
          </Button>
        </>
      }
    >
      <CompanyFields values={values} onChange={setValues} />
    </Dialog>
  )
}

interface CompanyValues {
  name: string
  industry: string
  size: string
  city: string
  website: string
  about: string
}

function CompanyFields({
  values,
  onChange,
}: {
  values: CompanyValues
  onChange: (values: CompanyValues) => void
}) {
  const set = (patch: Partial<CompanyValues>) => onChange({ ...values, ...patch })

  return (
    <div className="space-y-4">
      <Input
        label="Nama perusahaan"
        required
        value={values.name}
        onChange={(event) => set({ name: event.target.value })}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Bidang industri"
          value={values.industry}
          onChange={(event) => set({ industry: event.target.value })}
          placeholder="Teknologi Informasi"
        />
        <Select label="Jumlah karyawan" value={values.size} onChange={(event) => set({ size: event.target.value })}>
          <option value="">Belum diisi</option>
          {sizeOptions.map((size) => (
            <option key={size} value={size}>
              {size} orang
            </option>
          ))}
        </Select>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Input label="Kota" value={values.city} onChange={(event) => set({ city: event.target.value })} />
        <Input
          label="Situs web"
          placeholder="https://…"
          value={values.website}
          onChange={(event) => set({ website: event.target.value })}
        />
      </div>

      <Textarea
        label="Tentang perusahaan"
        rows={5}
        value={values.about}
        onChange={(event) => set({ about: event.target.value })}
        placeholder="Apa yang dikerjakan perusahaan, siapa pelanggannya, dan seperti apa cara kerja timnya."
      />
    </div>
  )
}

function CompanyProfileForm({ companyId }: { companyId: string }) {
  const { data: companies } = useMyCompanies()
  const update = useUpdateCompany(companyId)
  const toast = useToast()

  const company = companies?.find((item) => item.id === companyId)
  const [values, setValues] = useState<CompanyValues>({
    name: '',
    industry: '',
    size: '',
    city: '',
    website: '',
    about: '',
  })

  useEffect(() => {
    if (!company) return
    setValues({
      name: company.name,
      industry: company.industry,
      size: company.size,
      city: company.city,
      website: company.website,
      about: company.about,
    })
  }, [company])

  return (
    <Card>
      <CardHeader title="Profil perusahaan" description="Ditampilkan pada setiap lowongan yang Anda pasang." />
      <CardBody className="space-y-4">
        <CompanyFields values={values} onChange={setValues} />
        <div className="flex justify-end">
          <Button
            loading={update.isPending}
            onClick={() =>
              update.mutate(
                { ...values, website: values.website || undefined, size: values.size || undefined },
                {
                  onSuccess: () => toast.success('Profil perusahaan tersimpan'),
                  onError: (error) =>
                    toast.error('Gagal menyimpan', error instanceof ApiError ? error.message : undefined),
                },
              )
            }
          >
            Simpan perubahan
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}

function CompanyTeam({ companyId }: { companyId: string }) {
  const { data: members } = useCompanyMembers(companyId)
  const addMember = useAddCompanyMember(companyId)
  const removeMember = useRemoveCompanyMember(companyId)
  const toast = useToast()
  const [email, setEmail] = useState('')

  return (
    <Card>
      <CardHeader
        title="Tim perekrut"
        description="Perekrut yang ditambahkan dapat memasang lowongan dan meninjau pelamar atas nama perusahaan."
      />
      <CardBody className="space-y-4">
        <div className="flex gap-2">
          <Input
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="email perekrut yang sudah punya akun Skava"
            type="email"
          />
          <Button
            loading={addMember.isPending}
            onClick={() =>
              addMember.mutate(email, {
                onSuccess: () => {
                  toast.success('Perekrut ditambahkan')
                  setEmail('')
                },
                onError: (error) =>
                  toast.error('Gagal menambahkan', error instanceof ApiError ? error.message : undefined),
              })
            }
          >
            Tambahkan
          </Button>
        </div>

        <ul className="divide-y divide-[var(--border)]">
          {members?.map((member) => (
            <li key={member.user_id} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">{member.full_name}</p>
                <p className="text-xs text-muted">{member.email}</p>
              </div>
              <div className="flex items-center gap-3">
                <Badge tone={member.role === 'owner' ? 'brand' : 'neutral'}>
                  {member.role === 'owner' ? 'Pemilik' : 'Perekrut'}
                </Badge>
                {member.role !== 'owner' ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-danger"
                    onClick={() =>
                      removeMember.mutate(member.user_id, {
                        onSuccess: () => toast.info('Perekrut dikeluarkan'),
                      })
                    }
                  >
                    Keluarkan
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  )
}

function VacancyDialog({
  companyId,
  open,
  onClose,
}: {
  companyId: string
  open: boolean
  onClose: () => void
}) {
  const createVacancy = useCreateVacancy(companyId)
  const { data: categories } = useMarketplaceCategories()
  const toast = useToast()

  const [values, setValues] = useState<VacancyInput>({
    title: '',
    description: '',
    requirements: '',
    category: '',
    skills: [],
    employment_type: 'full_time',
    work_arrangement: 'onsite',
    experience_level: 'entry',
    city: '',
    salary_min: 0,
    salary_max: 0,
    salary_visible: true,
    openings: 1,
  })
  const [skillDraft, setSkillDraft] = useState('')

  useEffect(() => {
    if (categories?.length && !values.category) {
      setValues((current) => ({ ...current, category: categories[0]?.slug ?? '' }))
    }
  }, [categories, values.category])

  const set = (patch: Partial<VacancyInput>) => setValues((current) => ({ ...current, ...patch }))

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Pasang lowongan"
      description="Simpan sebagai draf dulu; tayangkan setelah isinya lengkap."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            loading={createVacancy.isPending}
            disabled={values.title.trim().length < 4}
            onClick={() =>
              createVacancy.mutate(values, {
                onSuccess: () => {
                  toast.success('Lowongan tersimpan sebagai draf', 'Lengkapi lalu tayangkan dari halaman pelamar.')
                  onClose()
                },
                onError: (error) =>
                  toast.error('Gagal menyimpan lowongan', error instanceof ApiError ? error.message : undefined),
              })
            }
          >
            Simpan draf
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Nama posisi"
          required
          value={values.title}
          onChange={(event) => set({ title: event.target.value })}
          placeholder="Misalnya: Data Analyst"
        />

        <Textarea
          label="Uraian pekerjaan"
          rows={6}
          value={values.description}
          onChange={(event) => set({ description: event.target.value })}
          hint="Minimal 80 karakter sebelum lowongan dapat ditayangkan."
          placeholder="Apa yang akan dikerjakan sehari-hari, dengan siapa bekerja, dan hasil apa yang diharapkan."
        />

        <Textarea
          label="Kualifikasi"
          rows={4}
          value={values.requirements}
          onChange={(event) => set({ requirements: event.target.value })}
          hint="Minimal 40 karakter. Pisahkan antara syarat wajib dan nilai tambah."
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <Select label="Bidang" value={values.category} onChange={(event) => set({ category: event.target.value })}>
            {categories?.map((category) => (
              <option key={category.slug} value={category.slug}>
                {category.name}
              </option>
            ))}
          </Select>
          <Select
            label="Jenis"
            value={values.employment_type}
            onChange={(event) => set({ employment_type: event.target.value })}
          >
            <option value="full_time">Penuh waktu</option>
            <option value="part_time">Paruh waktu</option>
            <option value="contract">Kontrak</option>
            <option value="internship">Magang</option>
            <option value="freelance">Lepas</option>
          </Select>
          <Select
            label="Lokasi kerja"
            value={values.work_arrangement}
            onChange={(event) => set({ work_arrangement: event.target.value })}
          >
            <option value="onsite">Di kantor</option>
            <option value="hybrid">Hibrida</option>
            <option value="remote">Jarak jauh</option>
          </Select>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Select
            label="Tingkat"
            value={values.experience_level}
            onChange={(event) => set({ experience_level: event.target.value })}
          >
            <option value="entry">Pemula</option>
            <option value="junior">Junior</option>
            <option value="mid">Menengah</option>
            <option value="senior">Senior</option>
            <option value="lead">Lead</option>
          </Select>
          <Input label="Kota" value={values.city} onChange={(event) => set({ city: event.target.value })} />
          <Input
            label="Jumlah posisi"
            type="number"
            min={1}
            value={values.openings}
            onChange={(event) => set({ openings: Number(event.target.value) })}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Gaji minimum"
            type="number"
            min={0}
            step={500000}
            prefix="Rp"
            value={values.salary_min || ''}
            onChange={(event) => set({ salary_min: Number(event.target.value) })}
          />
          <Input
            label="Gaji maksimum"
            type="number"
            min={0}
            step={500000}
            prefix="Rp"
            value={values.salary_max || ''}
            onChange={(event) => set({ salary_max: Number(event.target.value) })}
          />
        </div>

        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-line p-3.5">
          <input
            type="checkbox"
            checked={values.salary_visible}
            onChange={(event) => set({ salary_visible: event.target.checked })}
            className="mt-0.5 size-4 accent-[var(--brand)]"
          />
          <span>
            <span className="block text-sm font-semibold text-ink">Tampilkan rentang gaji</span>
            <span className="mt-0.5 block text-sm text-muted">
              Lowongan yang menampilkan gaji menerima pelamar yang lebih sesuai. Bila dimatikan, angkanya tidak
              dikirim ke klien sama sekali.
            </span>
          </span>
        </label>

        <div>
          <label htmlFor="vacancy-skill" className="text-sm font-semibold text-ink">
            Keahlian yang dicari
          </label>
          <div className="mt-1.5 flex gap-2">
            <Input
              id="vacancy-skill"
              value={skillDraft}
              onChange={(event) => setSkillDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return
                event.preventDefault()
                const value = skillDraft.trim().toLowerCase()
                if (!value || values.skills.includes(value)) return
                set({ skills: [...values.skills, value] })
                setSkillDraft('')
              }}
              placeholder="sql, figma, go…"
            />
          </div>
          {values.skills.length > 0 ? (
            <ul className="mt-3 flex flex-wrap gap-2">
              {values.skills.map((skill) => (
                <li key={skill}>
                  <button
                    type="button"
                    onClick={() => set({ skills: values.skills.filter((item) => item !== skill) })}
                    className="rounded-full bg-brand-soft px-3 py-1 text-sm font-medium text-brand-ink"
                  >
                    {skill} ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </Dialog>
  )
}
